import { createClient } from '@sanity/client';
import imageUrlBuilder from '@sanity/image-url';
import type {
  HeroContent,
  AboutTrailerContent,
  Product,
  ProductsPageContent,
  // Service,
  ServicesPageContent,
  TeamMember,
  TeamPageContent,
  Partner,
  AboutContent,
  ContactInfo,
  ContactPageContent,
  SiteSettings,
  ImageSlideContent,
  QualitySlideContent,
  MapSlideContent,
  HomeStat,
  CustomGradingContent,
  GrainSpecificationsContent,
  AboutPageContent,
  ServicesCutInContent,
  TrustCoreContent,
  ProductsBentoContent,
  TrackRecordContent,
  PartnersSectionContent,
} from './types';

// Sanity client configuration
// TODO: Add your Sanity project ID and dataset after setting up Sanity Studio
export const client = createClient({
  projectId: import.meta.env.VITE_SANITY_PROJECT_ID || 'gu5pbwq9',
  dataset: import.meta.env.VITE_SANITY_DATASET || 'production',
  useCdn: true,
  apiVersion: '2024-01-01',
});

// Image URL builder helper
const builder = imageUrlBuilder(client);

export function urlFor(source: any) {
  return builder.image(source);
}

// Fetch functions with error handling

export async function fetchHeroContent(): Promise<HeroContent | null> {
  try {
    const query = `*[_type == "hero" && isActive == true][0]{
      title,
      titleHighlight,
      subtitle,
      stats[]{value, label, suffix},
      ctas[]{text, href, variant},
      isActive
    }`;
    const result = await client.fetch(query);
    return result;
  } catch (error) {
    console.error('Error fetching hero content:', error);
    return null;
  }
}

export async function fetchHeroSlides(): Promise<{
  imageSlide: ImageSlideContent | null;
  qualitySlide: QualitySlideContent | null;
  mapSlide: MapSlideContent | null;
  useCarousel: boolean;
}> {
  try {
    const query = `*[_type == "hero" && isActive == true][0]{
      useCarousel,
      imageSlide{ order, phrases[]{ line1, line2 }, "imageUrl": imageSlide.image.asset->url, brandLabel, link },
      mapSlide{
        order,
        title,
        subtitle,
        description,
        countries[]{
          name,
          highlightColor,
          customColor,
          "highlight": coalesce(customColor, highlightColor)
        }
      },
      qualitySlide{ order, title, tagline, points, metrics[]{ value, label }, "videoUrl": qualitySlide.video.asset->url }
    }`;
    const result = await client.fetch(query);
    return {
      imageSlide: result?.imageSlide ?? null,
      qualitySlide: result?.qualitySlide ?? null,
      mapSlide: result?.mapSlide ?? null,
      useCarousel: result?.useCarousel ?? true,
    };
  } catch (error) {
    console.error('Error fetching hero slides:', error);
    return { imageSlide: null, qualitySlide: null, mapSlide: null, useCarousel: true };
  }
}

export async function fetchAboutTrailer(): Promise<AboutTrailerContent | null> {
  try {
    const query = `*[_type == "aboutTrailer" && isActive == true][0]{
      title,
      description,
      highlights[]{icon, title, description},
      ctaText,
      ctaLink,
      mediaType,
      "mediaUrl": media.asset->url,
      isActive
    }`;
    const result = await client.fetch(query);
    return result;
  } catch (error) {
    console.error('Error fetching about trailer:', error);
    return null;
  }
}

export async function fetchProducts(): Promise<Product[]> {
  try {
    const query = `*[_type == "product" && isActive == true] | order(order asc){
      id,
      name,
      description,
      icon,
      "image": image.asset->url,
      grades,
      bulkCapacity,
      packaging,
      delivery,
      features,
      isActive
    }`;
    const result = await client.fetch(query);
    return result;
  } catch (error) {
    console.error('Error fetching products:', error);
    return [];
  }
}

export async function fetchProductsPage(): Promise<ProductsPageContent | null> {
  try {
    const query = `*[_type == "productsPage"][0]{
      headerTitle,
      headerHighlight,
      headerDescription,
      capacityTitle,
      capacityDescription,
      capacityStats,
      ctaTitle,
      ctaDescription,
      ctaButton1Text,
      ctaButton1Link,
      ctaButton2Text,
      ctaButton2Link
    }`;
    const result = await client.fetch(query);
    return result;
  } catch (error) {
    console.error('Error fetching products page content:', error);
    return null;
  }
}

export async function fetchServicesPage(): Promise<ServicesPageContent | null> {
  try {
    const query = `*[_type == "servicesPage"][0]{
      headerTitle,
      headerHighlight,
      headerDescription,
      services[]{title, description, icon, features},
      howWeWorkTitle,
      howWeWorkDescription,
      processSteps[]{number, title, description},
      deliveryTitle,
      deliveryDescription,
      howWeDeliverPoints,
      contractedClientsPoints,
      "deliveryImage": deliveryImage.asset->url,
      ctaTitle,
      ctaDescription,
      ctaPoints,
      "ctaVideo": ctaVideo.asset->url
    }`;
    const result = await client.fetch(query);
    return result;
  } catch (error) {
    console.error('Error fetching services page content:', error);
    return null;
  }
}

export async function fetchTeamMembers(): Promise<TeamMember[]> {
  try {
    const query = `*[_type == "teamMember" && isActive == true] | order(order asc){
      name,
      role,
      bio,
      "image": image.asset->url,
      isActive
    }`;
    const result = await client.fetch(query);
    return result;
  } catch (error) {
    console.error('Error fetching team members:', error);
    return [];
  }
}

export async function fetchTeamPage(): Promise<TeamPageContent | null> {
  try {
    const query = `*[_type == "teamPage"][0]{
      headerTitle,
      headerHighlight,
      headerDescription,
      ctaTitle,
      ctaDescription,
      ctaButtonText,
      ctaEmail
    }`;
    const result = await client.fetch(query);
    return result;
  } catch (error) {
    console.error('Error fetching team page content:', error);
    return null;
  }
}

export async function fetchPartners(): Promise<Partner[]> {
  try {
    const query = `*[_type == "partner" && isActive == true] | order(order asc){
      name,
      "logo": logo.asset->url,
      category,
      isActive
    }`;
    const result = await client.fetch(query);
    return result;
  } catch (error) {
    console.error('Error fetching partners:', error);
    return [];
  }
}

export async function fetchAboutContent(): Promise<AboutContent | null> {
  try {
    const query = `*[_type == "about" && isActive == true][0]{
      mainTitle,
      story,
      founded,
      regions,
      coreValues[]{title, description, icon},
      promise,
      isActive
    }`;
    const result = await client.fetch(query);
    return result;
  } catch (error) {
    console.error('Error fetching about content:', error);
    return null;
  }
}

export async function fetchContactInfo(): Promise<ContactInfo | null> {
  try {
    const query = `*[_type == "contactInfo"][0]{
      email,
      phone,
      address,
      socialLinks[]{platform, url}
    }`;
    const result = await client.fetch(query);
    return result;
  } catch (error) {
    console.error('Error fetching contact info:', error);
    return null;
  }
}

export async function fetchContactPage(): Promise<ContactPageContent | null> {
  try {
    const query = `*[_type == "contactInfo"][0]{
      "headerTitle": pageHeaderTitle,
      "headerHighlight": pageHeaderHighlight,
      "headerDescription": pageHeaderDescription,
      "mediaImages": pageMediaImages[].asset->url,
      "whyChooseTitle": pageWhyChooseTitle,
      "whyChoosePoints": pageWhyChoosePoints,
      "formTitle": pageFormTitle,
      "submitButtonText": pageSubmitButtonText,
      "successMessage": pageSuccessMessage
    }`;
    const result = await client.fetch(query);
    return result;
  } catch (error) {
    console.error('Error fetching contact page content:', error);
    return null;
  }
}

export async function fetchSiteSettings(): Promise<SiteSettings | null> {
  try {
    const query = `*[_type == "siteSettings"][0]{
      grainAnimationEnabled,
      siteName,
      heroCarouselEnabled,
      whatsappNumber
    }`;
    const result = await client.fetch(query);
    return result;
  } catch (error) {
    console.error('Error fetching site settings:', error);
    return null;
  }
}

export async function fetchHomeStats(): Promise<HomeStat[] | null> {
  try {
    const query = `*[_type == "homeStats"][0]{ stats[]{value, display, label, prefix, isDecimal} }`;
    const result = await client.fetch(query);
    return result?.stats ?? null;
  } catch (error) {
    console.error('Error fetching home stats:', error);
    return null;
  }
}

export async function fetchCustomGrading(): Promise<CustomGradingContent | null> {
  try {
    const query = `*[_type == "customGrading"][0]{
      title,
      titleHighlight,
      subtitle,
      intro,
      "images": images[].asset->url,
      whoTitle,
      whoDescription,
      useCases[]{icon, title, description},
      processTitle,
      processDescription,
      steps[]{number, title, description, icon},
      tiersTitle,
      tiersDescription,
      tiers[]{commodity, grades},
      legend,
      docTitle,
      docBody,
      ctaTitle,
      ctaBody,
      ctaPrimaryText,
      ctaPrimaryLink,
      ctaSecondaryText,
      ctaSecondaryLink
    }`;
    const result = await client.fetch(query);
    return result;
  } catch (error) {
    console.error('Error fetching custom grading content:', error);
    return null;
  }
}

export async function fetchGrainSpecifications(): Promise<GrainSpecificationsContent | null> {
  try {
    const query = `*[_type == "grainSpecifications"][0]{
      title,
      titleHighlight,
      description,
      "images": images[].asset->url,
      specs[]{commodity, moistureMax, purityMin, gradeStandards, rejectionCriteria, specialNotes}
    }`;
    const result = await client.fetch(query);
    return result;
  } catch (error) {
    console.error('Error fetching grain specifications content:', error);
    return null;
  }
}

export async function fetchAboutPage(): Promise<AboutPageContent | null> {
  try {
    const query = `*[_type == "aboutPage"][0]{
      headerTitle,
      headerHighlight,
      storyTitle,
      storyParagraphs,
      missionTitle,
      missionQuote,
      storyMediaType,
      "storyMediaUrl": storyMedia.asset->url,
      foundationTitle,
      foundationStats[]{value, label},
      valuesTitle,
      valuesDescription,
      coreValues[]{icon, title, description},
      strengthsTitle,
      strengths[]{title, description},
      processTitle,
      processDescription,
      processSteps[]{number, title, description},
      relationshipsTitle,
      relationshipsDescription,
      network[]{icon, label},
      benefitsTitle,
      benefits,
      relationshipsNote,
      "relationshipsImage": relationshipsImage.asset->url,
      qualityTitle,
      qualityDescription,
      "qualityImages": qualityImages[].asset->url,
      qualityChecks,
      certificationsTitle,
      certificationsDescription,
      certifications[]{title, body, "image": image.asset->url},
      promiseTitle,
      promiseBody
    }`;
    const result = await client.fetch(query);
    return result;
  } catch (error) {
    console.error('Error fetching about page content:', error);
    return null;
  }
}

export async function fetchServicesCutIn(): Promise<ServicesCutInContent | null> {
  try {
    const query = `*[_type == "servicesCutIn"][0]{ label, title, "videoUrl": video.asset->url, steps[]{number, title, desc}, ctaText, ctaLink }`;
    const result = await client.fetch(query);
    return result;
  } catch (error) {
    console.error('Error fetching services cut in content:', error);
    return null;
  }
}

export async function fetchTrustCore(): Promise<TrustCoreContent | null> {
  try {
    const query = `*[_type == "trustCore"][0]{ title, description, ctaText, ctaLink, stats[]{value, label}, centerTop, centerBottom }`;
    const result = await client.fetch(query);
    return result;
  } catch (error) {
    console.error('Error fetching trust core content:', error);
    return null;
  }
}

export async function fetchProductsBento(): Promise<ProductsBentoContent | null> {
  try {
    const query = `*[_type == "productsBento"][0]{
      title,
      description,
      ctaText,
      ctaLink,
      "products": select(
        count(featuredProducts) > 0 => featuredProducts[]->{ name, tag, "image": image.asset->url },
        *[_type == "product" && isActive == true] | order(order asc) [0...5] { name, tag, "image": image.asset->url }
      )
    }`;
    const result = await client.fetch(query);
    return result;
  } catch (error) {
    console.error('Error fetching products bento content:', error);
    return null;
  }
}

export async function fetchTrackRecord(): Promise<TrackRecordContent | null> {
  try {
    const query = `*[_type == "trackRecordPage"][0]{
      headerTitle,
      headerHighlight,
      headerDescription,
      headerNote,
      mainStats[]{value, label, suffix},
      additionalMetrics[]{metric, value},
      "photoRowImages": photoRowImages[].asset->url,
      testimonialsTitle,
      testimonialsDescription,
      testimonials[]{quote, author, role, company, "image": image.asset->url},
      timelineTitle,
      timelineSubtitle,
      timelineDescription,
      "milestonesBackgroundImage": milestonesBackgroundImage.asset->url,
      milestones[]{year, title, description},
      ctaTitle,
      ctaDescription,
      ctaPrimaryText,
      ctaPrimaryLink,
      ctaSecondaryText,
      ctaSecondaryLink
    }`;
    const result = await client.fetch(query);
    return result;
  } catch (error) {
    console.error('Error fetching track record content:', error);
    return null;
  }
}

export async function fetchPartnersSection(): Promise<PartnersSectionContent | null> {
  try {
    const query = `*[_type == "partnersSection"][0]{ title, description }`;
    const result = await client.fetch(query);
    return result;
  } catch (error) {
    console.error('Error fetching partners section content:', error);
    return null;
  }
}
